import React from 'react';
import {
  AssignmentsItemModel,
} from 'forms/sections/history/employment/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
} from 'types/props';
import { FormTypes } from 'constants/formTypes';
import Field from 'components/Form/Field';
import DateRange from 'components/Form/DateRange';
import Telephone from 'components/Form/Telephone';
import Text from 'components/Form/Text';
import Dropdown from 'components/Form/Dropdown';
import { setAttribute } from 'components/Form/shared/setAttribute';
import {
  assignedDutyStatusTypes,
} from 'constants/enums/employmentOptions';
import { i18n } from 'config/locales';

interface Props extends ValidationElementProps, Partial<AssignmentsItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView

  formType?: FormTypes
}

export default class AssignmentItem extends ValidationElement<Props> {
  update = (queue: any) => {
    this.props.onUpdate({
      AssignedUnitOrg: this.props.AssignedUnitOrg,
      AssignedUnitOrgDateRange: this.props.AssignedUnitOrgDateRange,
      AssignedUnitOrgTelephone: this.props.AssignedUnitOrgTelephone,
      AssignedUnitOrgDutyStatus: this.props.AssignedUnitOrgDutyStatus,
      AssignedUnitOrgTitle: this.props.AssignedUnitOrgTitle,
      Has: this.props.Has,
      ...queue,
    });
  };

  updateAssignedUnitOrg = (values: AssignmentsItemModel['AssignedUnitOrg']) => {
    this.update({
      AssignedUnitOrg: values,
    });
  };

  updateAssignedUnitOrgDateRange = (values: AssignmentsItemModel['AssignedUnitOrgDateRange']) => {
    this.update({
      AssignedUnitOrgDateRange: values,
    });
  };

  updateAssignedUnitOrgTelephone = (values: AssignmentsItemModel['AssignedUnitOrgTelephone']) => {
    this.update({
      AssignedUnitOrgTelephone: values,
    });
  };

  updateAssignedUnitOrgDutyStatus = (values: AssignmentsItemModel['AssignedUnitOrgDutyStatus']) => {
    this.update({
      AssignedUnitOrgDutyStatus: values,
    });
  };

  updateAssignedUnitOrgTitle = (values: AssignmentsItemModel['AssignedUnitOrgTitle']) => {
    this.update({
      AssignedUnitOrgTitle: values,
    });
  };

  render() {
    const { formType } = this.props;
    const prefix = 'history.pvq.employment';
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;

    return (
      <>

        <Field
          attribute={setAttribute(attribute, 'AssignedUnitOrg')}
          title={i18n.t(`${prefix}.default.heading.assignedUnitOrg`)}
          titleSize="h4"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          exactAttribute
        >
          <Text
            attribute={setAttribute(attribute, 'AssignedUnitOrg')}
            name="AssignedUnitOrg"
            {...this.props.AssignedUnitOrg}
            onUpdate={this.updateAssignedUnitOrg}
            onError={this.props.onError}
            className="text full-width assignedunit"
            required={this.props.required}
            exactAttribute
          />
        </Field>

        <Field
          attribute={`${attribute}.AssignedUnitOrgDateRange`}
          title={i18n.t(`${prefix}.default.heading.assignedUnitOrgDateRange`)}
          titleSize="h4"
          adjustFor="daterange"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            attribute={`${attribute}.AssignedUnitOrgDateRange`}
            name="AssignedUnitOrgDateRange"
            {...this.props.AssignedUnitOrgDateRange}
            onUpdate={this.updateAssignedUnitOrgDateRange}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.AssignedUnitOrgTelephone`}
          title={i18n.t(`${prefix}.default.heading.assignedUnitOrgTelephone`)}
          titleSize="h4"
          className="override-required"
          adjustFor="telephone"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Telephone
            attribute={`${attribute}.AssignedUnitOrgTelephone`}
            name="AssignedUnitOrgTelephone"
            {...this.props.AssignedUnitOrgTelephone}
            onUpdate={this.updateAssignedUnitOrgTelephone}
            allowNotApplicable={false}
            onError={this.props.onError}
            required={this.props.required}
            showTimeOfDay
            showNumberType
            timeOfDayLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.timeOfDayLabel')}
          />
        </Field>

        <Field
          title={i18n.t(`${prefix}.default.heading.assignedUnitOrgDutyStatus`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AssignedUnitOrgDutyStatus`}
        >
          <Dropdown
            placeholder={this.props.AssignedUnitOrgDutyStatus?.value}
            attribute={`${attribute}.AssignedUnitOrgDutyStatus`}
            name="AssignedUnitOrgDutyStatus"
            {...this.props.AssignedUnitOrgDutyStatus}
            onUpdate={this.updateAssignedUnitOrgDutyStatus}
            onError={this.props.onError}
            sort={false}
            value={this.props.AssignedUnitOrgDutyStatus?.value}
            required={this.props.required}
            suggestionsRenderProvided
          >
            {assignedDutyStatusTypes.map((x, index) => (
              <option key={`${x.key}-${index}`} value={`${x.value}`}>
                {i18n.t(`military.pvq.history.label.${x.i18nField}`)}
              </option>
            ))}
          </Dropdown>
        </Field>
        <Field
          attribute={`${attribute}.AssignedUnitOrgTitle`}
          title={i18n.t(`${prefix}.default.heading.assignedUnitOrgTitle`)}
          titleSize="h4"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            attribute={`${attribute}.AssignedUnitOrgTitle`}
            name="AssignedUnitOrgTitle"
            {...this.props.AssignedUnitOrgTitle}
            onUpdate={this.updateAssignedUnitOrgTitle}
            onError={this.props.onError}
            className="text full-width assignedunit"
            required={this.props.required}
          />
        </Field>
      </>

    );
  }
}

AssignmentItem.defaultProps = {
  AssignedUnitOrg: {},
  AssignedUnitOrgDateRange: {},
  AssignedUnitOrgTelephone: {},
  AssignedUnitOrgDutyStatus: {},
  AssignedUnitOrgTitle: {},
  Has: {},

  onUpdate: () => { },
  onError: (value, arr) => arr,
};
